/**
 * Who a session is expecting, and which of them are still unclaimed.
 *
 * Mirrors mobile/src/features/group/guestList.ts, like the rest of the group
 * protocol, so a phone and a browser agree about what an invite means.
 *
 * A session started for people who have eaten together before carries their
 * names and member keys, so whoever opens the link can say which one they are
 * instead of typing into an empty box. That matters more here than anywhere
 * else: this page is what an invite link opens, so this is where almost every
 * guest arrives.
 *
 * Identity in a session is a per-browser id. The same person opening one
 * invite from iMessage and the next from an in-app browser arrives as two
 * different people, and the host's group history — keyed on exactly that set
 * of ids — files the second dinner as a group that has never met. Claiming a
 * name pins them to the id the host already has, which is what lets a group
 * accumulate a history instead of restarting it every time.
 */

/**
 * @typedef {object} Guest
 * @property {string} id    The member key the claimer joins under.
 * @property {string} name
 */

/**
 * The expected guests nobody has taken yet, alphabetically.
 *
 * An invite whose key is already among the members is gone: either that person
 * is here, or somebody has claimed to be them. Removing it is what stops two
 * people both being Sam — a collision that would not just confuse the roster
 * but overwrite one of their votes, since votes are stored per member key.
 *
 * @param {Record<string, {name: string}> | undefined} invites
 * @param {Record<string, unknown> | undefined} members
 * @returns {Guest[]}
 */
export function unclaimedGuests(invites, members) {
  return Object.entries(invites ?? {})
    .filter(([id]) => !(id in (members ?? {})))
    .map(([id, invite]) => ({ id, name: invite?.name?.trim() || 'Guest' }))
    .sort((a, b) => a.name.localeCompare(b.name));
}

/**
 * Resolves an installed-app identity to the member key this session already
 * knows. The stable id is strongest. A unique name match handles older groups
 * created before the recipient voted through the installed app; ambiguity
 * falls back to the app's own id so two people with the same name never share
 * a vote bucket.
 *
 * @param {Record<string, {name?: string}> | undefined} invites
 * @param {Record<string, {name?: string}> | undefined} members
 * @param {{id: string, name: string}} identity
 * @returns {string}
 */
export function memberIdForAppIdentity(invites, members, identity) {
  if (identity.id in (members ?? {}) || identity.id in (invites ?? {})) return identity.id;

  const normalizedName = identity.name.trim().toLocaleLowerCase();
  const matchingMembers = Object.entries(members ?? {}).filter(
    ([, member]) => member?.name?.trim().toLocaleLowerCase() === normalizedName
  );
  if (matchingMembers.length === 1) return matchingMembers[0][0];

  const matchingGuests = unclaimedGuests(invites, members).filter(
    (guest) => guest.name.trim().toLocaleLowerCase() === normalizedName
  );
  return matchingGuests.length === 1 ? matchingGuests[0].id : identity.id;
}
